import React, { useEffect, useMemo, useState } from 'react';
import Pagination from '../pagination/Pagination';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  /** Что рендерить в ячейке. */
  render: (item: T) => React.ReactNode;
  /** Дополнительные классы на td (ширина, выравнивание и т.п.). */
  className?: string;
  /** Дополнительные классы на th. */
  headerClassName?: string;
  /** Если true — клик по этой ячейке не триггерит onRowClick. */
  stopRowClick?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyAccessor: (item: T) => string | number;
  /** Клик по строке (например, открыть детали). */
  onRowClick?: (item: T) => void;
  /** Состояние «загружается» — показываем скелетон. */
  loading?: boolean;
  /** Что показать, если данных нет. */
  emptyMessage?: string;
  /** Дополнительные стили на каждую строку — например, выделить недавнюю. */
  rowClassName?: (item: T) => string | undefined;
  /** Сколько скелетон-строк рисовать. */
  skeletonRows?: number;
  /**
   * Размер страницы. Если задан и > 0 — включается клиентская пагинация:
   * рендерим только текущий slice, снизу появляется компактный
   * `Pagination`-блок. По умолчанию 0 (нет пагинации).
   */
  pageSize?: number;
  /**
   * Раскрываемые строки. Если оба пропса заданы и `isExpanded(item)`
   * вернёт true — под строкой появляется дополнительная `<tr>` с
   * содержимым `renderExpanded`. Сама строка получит лёгкий хайлайт.
   * Toggle делает родитель (см. `onRowClick`).
   */
  renderExpanded?: (item: T) => React.ReactNode;
  isExpanded?: (item: T) => boolean;
}

function DataTable<T>({
  columns,
  data,
  keyAccessor,
  onRowClick,
  loading = false,
  emptyMessage = 'Нет данных',
  rowClassName,
  skeletonRows = 4,
  pageSize = 0,
  renderExpanded,
  isExpanded,
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);

  const isPaginated = pageSize > 0;
  const totalPages = isPaginated
    ? Math.max(1, Math.ceil(data.length / pageSize))
    : 1;

  // Если данные сократились/обновились так, что текущая страница вышла
  // за границы (например, после фильтрации/удаления) — съезжаем на
  // последнюю существующую.
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const visibleRows = useMemo(() => {
    if (!isPaginated) return data;
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, isPaginated, currentPage, pageSize]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <div key={i} className="h-14 bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-gray-400 text-center py-8">{emptyMessage}</p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        {/*
          min-w-[640px] заставляет таблицу не схлопываться на узких
          экранах: вместо squashed-колонок появляется горизонтальный
          скролл, что для табличного контента удобнее.
        */}
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider ${
                    c.headerClassName ?? ''
                  }`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((item) => {
              const id = keyAccessor(item);
              const clickable = Boolean(onRowClick);
              const handleRowClick = () => {
                if (onRowClick) onRowClick(item);
              };
              const expanded = Boolean(
                isExpanded && renderExpanded && isExpanded(item)
              );
              return (
                <React.Fragment key={id}>
                  <tr
                    onClick={clickable ? handleRowClick : undefined}
                    className={`border-b border-gray-100 last:border-b-0 transition-colors ${
                      clickable ? 'cursor-pointer hover:bg-gray-50/50' : ''
                    } ${expanded ? 'bg-gray-50/70' : ''} ${
                      rowClassName?.(item) ?? ''
                    }`}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={`py-3 px-2 align-middle ${c.className ?? ''}`}
                        onClick={
                          c.stopRowClick
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {c.render(item)}
                      </td>
                    ))}
                  </tr>
                  {expanded && renderExpanded ? (
                    <tr className="border-b border-gray-100 last:border-b-0">
                      <td
                        colSpan={columns.length}
                        className="p-0 bg-gray-50/40"
                      >
                        {renderExpanded(item)}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {isPaginated && totalPages > 1 ? (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={data.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      ) : null}
    </>
  );
}

export default DataTable;
